import React, { createContext, useState, useContext, useEffect } from 'react';
import { UserData } from '../types/UserData';
// import { BackendAdapter } from '../adapters/BackendAdapter';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  token: string | null; // Explicitly expose token for useBackend.ts
  login: (userData: UserData) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
  updatePreferences: (preferences: Partial<UserData['preferences']>) => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  token: null,
  login: () => { },
  logout: async () => { },
  isLoading: true,
  updatePreferences: () => { },
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}



export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const storedUserData = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');

      if (token && storedUserData) {
        try {
          const parsedUser = JSON.parse(storedUserData);
          const userWithToken: UserData = {
            ...parsedUser,
            token: token,
            preferences: parsedUser.preferences || { darkMode: false, notifications: true },
          };
          setUser(userWithToken);
          setToken(token);
          // Set token in BackendAdapter
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('auth_token');
          sessionStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          sessionStorage.removeItem('user_data');
          localStorage.removeItem('sessionId'); // Clear session ID
          setUser(null);
          setToken(null);
        }
      } else {
        // Clear session ID if no valid token/user data
        localStorage.removeItem('sessionId');
      }

      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (user?.preferences?.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [user?.preferences?.darkMode]);

  const login = (userData: UserData) => {
    if (!userData.token) {
      console.error('Login error: No token provided in userData');
      return;
    }

    setUser(userData);
    setToken(userData.token);

    // Store in localStorage if it's already there (set by Login.tsx), otherwise use sessionStorage
    if (localStorage.getItem('auth_token')) {
      localStorage.setItem('auth_token', userData.token);
      localStorage.setItem('user_data', JSON.stringify(userData));
    } else {
      sessionStorage.setItem('auth_token', userData.token);
      sessionStorage.setItem('user_data', JSON.stringify(userData));
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint if sessionId exists
      const sessionId = localStorage.getItem('sessionId');
      if (sessionId && token) {
        await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ sessionId }),
        });
      }

      // Clear all state
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('user_data');
      localStorage.removeItem('sessionId');
      setUser(null);
      setToken(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updatePreferences = (newPreferences: Partial<UserData['preferences']>) => {
    if (!user) return;
    const updatedUser = {
      ...user,
      preferences: {
        ...user.preferences,
        ...newPreferences,
      },
    };
    setUser(updatedUser);
    // Store update in correct storage
    if (localStorage.getItem('auth_token')) {
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
    } else {
      sessionStorage.setItem('user_data', JSON.stringify(updatedUser));
    }
  };

  const value = {
    isAuthenticated: !!user && !!token,
    user,
    token,
    login,
    logout,
    isLoading,
    updatePreferences,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;